'use client';

import useBatchUpload, {
  UploadType,
} from '@/hooks/deep-mining/use-batch-upload';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from 'react';
import { toast } from 'sonner';
import { useI18n } from '@/hooks/use-i18n';

// 生成唯一ID
const generateId = () =>
  `upload_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

export interface UploadItem {
  file: File;
  id: string;
  status: UploadStatus;
  progress: number;
  object_key?: string;
  upload_url?: string;
  error?: Error;
  [key: string]: any;
}

// 上传配置选项
export interface UploadContextConfig {
  uploadType: UploadType;
  accept?: string;
  maxSize?: number;
  maxCount?: number;
  specifyExtMaxSize?: Record<string, number>;
  concurrency: number;
  // 自定义校验函数
  customValidation?: (
    file: File,
  ) => Promise<{ valid: boolean; reason?: string }>;
  // 成功/失败回调
  onUploadSuccess?: (files: UploadItem[]) => void | Promise<void>;
  onUploadError?: (error: Error, files: UploadItem[]) => void;
  // 单个文件的回调
  onItemSuccess?: (item: FILE.UploadItem) => void;
  onItemError?: (item: FILE.UploadItem) => void;
  onItemProgress?: (item: FILE.UploadItem) => void;
}

// Context 类型定义
interface UploadContextType {
  // 状态
  files: UploadItem[];
  uploadingFiles: UploadItem[];
  remainingSlots: number;

  // 操作方法
  onUploadSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUploadDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onUploadFiles: (files: File[]) => void;
  onDelete: (id: string) => void;
  onUploadRetry: (id: string) => void;
  onClear: () => void;

  // 工具方法
  checkFileValidity: (file: File) => { valid: boolean; reason?: string };
  getFileById: (id: string) => UploadItem | undefined;
}

const emptyFn = () => {};
const defaultValue: UploadContextType = {
  files: [],
  uploadingFiles: [],
  remainingSlots: 0,
  onUploadSelect: emptyFn,
  onUploadDrop: emptyFn,
  onUploadFiles: emptyFn,
  onDelete: emptyFn,
  onUploadRetry: emptyFn,
  onClear: emptyFn,
  checkFileValidity: () => ({ valid: true }),
  getFileById: () => undefined,
};

// 创建 Context
const UploadContext = createContext<UploadContextType | undefined>(undefined);

// Provider Props
interface UploadContextProviderProps {
  children: ReactNode;
  config: UploadContextConfig;
}

// Provider 组件
export const UploadContextProvider: React.FC<UploadContextProviderProps> = ({
  children,
  config,
}) => {
  const { t } = useI18n('common');
  const {
    uploadType,
    accept = '*',
    maxSize = 10 * 1024 * 1024,
    maxCount,
    specifyExtMaxSize = {},
    concurrency = 1,
    customValidation,
    onUploadSuccess,
    onUploadError,
    onItemSuccess: configOnItemSuccess,
    onItemError: configOnItemError,
    onItemProgress,
  } = config;

  // 文件状态管理
  const [files, _setFiles] = useState<UploadItem[]>([]);
  const filesRef = useRef<UploadItem[]>([]);
  const [remainingSlots, setRemainingSlots] = useState(maxCount);

  const setFiles = useCallback((newFiles: UploadItem[]) => {
    _setFiles(newFiles);
    filesRef.current = newFiles;
  }, []);

  // 处理单个文件上传成功
  const handleItemSuccess = useCallback(
    (item: FILE.UploadItem) => {
      const newFiles = filesRef.current.map((file) => {
        if (file.id === item.id) {
          return {
            ...file,
            ...item,
            status: 'success' as FILE.UploadStatus,
            progress: 100,
          };
        }
        return file;
      });
      setFiles(newFiles);
      configOnItemSuccess?.(item);

      // 检查是否所有文件都上传完成
      const allCompleted = newFiles.every(
        (f) => f.status === 'success' || f.status === 'error',
      );
      if (allCompleted) {
        const successFiles = newFiles.filter((f) => f.status === 'success');
        onUploadSuccess?.(successFiles);
      }
    },
    [setFiles, configOnItemSuccess, onUploadSuccess],
  );

  // 处理单个文件上传失败
  const handleItemError = useCallback(
    (item: FILE.UploadItem) => {
      const newFiles = filesRef.current.map((file) => {
        if (file.id === item.id) {
          return {
            ...file,
            ...item,
            status: 'error' as FILE.UploadStatus,
            error: item.error,
          };
        }
        return file;
      });
      setFiles(newFiles);
      configOnItemError?.(item);

      // 检查是否所有文件都上传完成
      const allCompleted = newFiles.every(
        (f) => f.status === 'success' || f.status === 'error',
      );
      if (allCompleted) {
        const errorFiles = newFiles.filter((f) => f.status === 'error');
        if (errorFiles.length > 0) {
          onUploadError?.(
            new Error(t('common:error.partialUploadFailed')),
            errorFiles,
          );
        }
      }
    },
    [setFiles, configOnItemError, onUploadError],
  );

  // 处理上传进度
  const handleItemProgress = useCallback(
    (item: FILE.UploadItem) => {
      const newFiles = filesRef.current.map((file) => {
        if (file?.id === item?.id) {
          return {
            ...file,
            ...item,
            progress: item.progress,
          };
        }
        return file;
      });
      setFiles(newFiles);
      onItemProgress?.(item);
    },
    [setFiles, onItemProgress],
  );

  // 使用 useBatchUpload hook
  const {
    checkFileValidity,
    validateFiles,
    addToQueue,
    cancelUpload,
    retryUpload,
    clearAll,
  } = useBatchUpload({
    uploadType,
    accept,
    maxSize,
    specifyExtMaxSize,
    concurrency,
    onItemSuccess: handleItemSuccess,
    onItemError: handleItemError,
    onItemProgress: handleItemProgress,
  });

  // 限制文件数量
  const limitFilesToAvailableSlots = useCallback(
    (files: File[]) => {
      // 如果不限制数量，直接返回所有文件
      if (maxCount === Infinity || maxCount === undefined) {
        return files;
      }

      if (remainingSlots <= 0) {
        toast.error(t('uploadContext.maxFilesReached', { maxCount }));
        return [];
      }
      if (files.length > remainingSlots) {
        toast.error(
          t('uploadContext.maxFilesSelectedFirst', {
            maxCount,
            remainingSlots,
          }),
        );
        return files.slice(0, remainingSlots);
      }
      return files;
    },
    [remainingSlots, maxCount, t],
  );

  // 处理文件上传
  const onUploadFiles = useCallback(
    async (_files: File[]) => {
      if (!_files || _files.length === 0) {
        return;
      }

      // 限制文件数量
      const availableFiles = limitFilesToAvailableSlots(_files);

      // 基础校验
      const validFiles = validateFiles(availableFiles);

      // 自定义校验
      if (customValidation) {
        const customValidatedFiles: File[] = [];
        for (const file of validFiles) {
          const { valid, reason } = await customValidation(file);
          if (valid) {
            customValidatedFiles.push(file);
          } else if (reason) {
            toast.error(reason);
          }
        }
        if (customValidatedFiles.length === 0) {
          return;
        }
        validFiles.splice(0, validFiles.length, ...customValidatedFiles);
      }

      // 创建上传文件对象
      const newFiles: UploadItem[] = validFiles.map((file) => ({
        id: generateId(),
        file,
        status: 'pending',
        progress: 0,
      }));

      // 更新文件列表
      setFiles([...filesRef.current, ...newFiles]);

      // 减少剩余槽位
      setRemainingSlots((prev) => prev - validFiles.length);

      // 添加到上传队列，由 useBatchUpload 内部处理状态
      addToQueue(newFiles);
    },
    [
      limitFilesToAvailableSlots,
      validateFiles,
      customValidation,
      setFiles,
      addToQueue,
    ],
  );

  // 处理文件选择
  const onUploadSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) {
        return;
      }
      onUploadFiles(Array.from(e.target.files));
      e.target.value = '';
    },
    [onUploadFiles],
  );

  // 处理拖拽上传
  const onUploadDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.dataTransfer.files) {
        return;
      }
      onUploadFiles(Array.from(e.dataTransfer.files));
    },
    [onUploadFiles],
  );

  // 删除文件
  const onDelete = useCallback(
    (id: string) => {
      const fileToDelete = filesRef.current.find((f) => f.id === id);
      if (!fileToDelete) {
        return;
      }

      // 如果正在上传，取消上传
      if (
        fileToDelete.status === 'uploading' ||
        fileToDelete.status === 'pending'
      ) {
        cancelUpload(id);
      }

      // 从列表中移除
      setFiles(filesRef.current.filter((f) => f.id !== id));

      // 增加剩余槽位
      setRemainingSlots((prev) => prev + 1);
    },
    [setFiles, cancelUpload],
  );

  // 重试上传
  const onUploadRetry = useCallback(
    (id: string) => {
      const fileToRetry = filesRef.current.find((f) => f.id === id);
      if (!fileToRetry || !fileToRetry.file) {
        return;
      }

      // 更新状态为 pending
      const newFiles = filesRef.current.map((file) => {
        if (file.id === id) {
          return {
            ...file,
            status: 'pending' as FILE.UploadStatus,
            error: undefined,
          };
        }
        return file;
      });
      setFiles(newFiles);

      // 重试
      retryUpload(id);
    },
    [setFiles, retryUpload],
  );

  // 清空所有文件
  const onClear = useCallback(() => {
    // 取消所有上传
    clearAll();

    const currentFileCount = filesRef.current.length;
    setFiles([]);

    // 恢复所有槽位
    setRemainingSlots((prev) => prev + currentFileCount);
  }, [setFiles, clearAll]);

  // 根据ID获取文件
  const getFileById = useCallback((id: string) => {
    return filesRef.current.find((f) => f.id === id);
  }, []);

  // 计算派生状态
  const uploadingFiles = files.filter((f) => f.status === 'uploading');

  // 提供上下文值
  const value: UploadContextType = {
    // 状态
    files,
    uploadingFiles,
    remainingSlots,

    // 操作方法
    onUploadSelect,
    onUploadDrop,
    onUploadFiles,
    onDelete,
    onUploadRetry,
    onClear,

    // 工具方法
    checkFileValidity,
    getFileById,
  };

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
};

// 自定义 Hook
export const useUpload = (): UploadContextType => {
  const context = useContext(UploadContext);
  if (context === undefined) {
    console.error('useUpload must be used within an UploadContextProvider');
    return defaultValue;
  }
  return context;
};
